import React, { useEffect, useState } from 'react';
import { TableSkeleton } from '../../../components/loading/skeletons';
import { adminListImpersonationLogs } from '../../impersonation/api/impersonationLogsApi';
import './ImpersonationLogs.css';

const PAGE_SIZE = 50;

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'success', label: 'Success' },
  { value: 'failure', label: 'Failure' },
  { value: 'end', label: 'End' }
];

const ImpersonationLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    adminEmail: '',
    targetUserEmail: '',
    companyName: '',
    status: ''
  });
  const [lastCursor, setLastCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchLogs = async (isLoadMore = false) => {
    try {
      setLoading(true);
      setError(null);
      const result = await adminListImpersonationLogs({
        adminEmail: filters.adminEmail,
        targetUserEmail: filters.targetUserEmail,
        companyName: filters.companyName,
        status: filters.status,
        limit: PAGE_SIZE,
        cursor: isLoadMore ? lastCursor : null,
      });
      const newLogs = result.logs || [];
      setLastCursor(result.nextCursor || null);
      setHasMore(Boolean(result.hasMore));
      if (isLoadMore) {
        setLogs((prev) => [...prev, ...newLogs]);
      } else {
        setLogs(newLogs);
      }
    } catch (err) {
      console.error('Error fetching impersonation logs:', err);
      setError(err.message || 'Failed to fetch impersonation logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolveUserEmail = (log) => log.targetUserEmail || '';

  const resolveCompanyName = (log) => log.companyName || '';

  const handleSearch = (e) => {
    e.preventDefault();
    setLastCursor(null);
    fetchLogs(false);
  };

  const handleReset = () => {
    setFilters({
      adminEmail: '',
      targetUserEmail: '',
      companyName: '',
      status: ''
    });
    setLastCursor(null);
    setTimeout(() => fetchLogs(false), 0);
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchLogs(true);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const deriveStatus = (log) => {
    if (log.status) return log.status; // legacy client logs
    if (log.action === 'end_impersonation') return 'end';
    if (typeof log.success === 'boolean') return log.success ? 'success' : 'failure';
    return 'unknown';
  };

  const formatStatusBadgeClass = (status) => {
    if (status === 'success') return 'status-badge status-success';
    if (status === 'failure' || status === 'end') return 'status-badge status-failed';
    return 'status-badge status-neutral';
  };

  const getStatusLabel = (status) => {
    if (!status || status === 'unknown') return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Client-side filtering for emails, company name, and status
  const filteredLogs = logs.filter(log => {
    const status = deriveStatus(log);

    const resolvedUserEmail = resolveUserEmail(log);
    const resolvedCompanyName = resolveCompanyName(log);

    const matchesAdmin = filters.adminEmail
      ? (log.adminEmail || '').toLowerCase().includes(filters.adminEmail.toLowerCase())
      : true;

    const matchesTarget = filters.targetUserEmail
      ? resolvedUserEmail.toLowerCase().includes(filters.targetUserEmail.toLowerCase())
      : true;

    const matchesCompany = filters.companyName
      ? resolvedCompanyName.toLowerCase().includes(filters.companyName.toLowerCase())
      : true;

    const matchesStatus = filters.status
      ? status === filters.status
      : true;

    return matchesAdmin && matchesTarget && matchesCompany && matchesStatus;
  });

  return (
    <div className="admin-page impersonation-logs-container">
      <div className="impersonation-logs-header">
        <h1>Impersonation Logs</h1>
        <p className="impersonation-logs-subtitle">
          Track which admins impersonated which users and companies, for auditing and security.
        </p>
      </div>

      <div className="impersonation-logs-filters">
        <form onSubmit={handleSearch}>
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="adminEmail">Admin Email</label>
              <input
                id="adminEmail"
                type="text"
                placeholder="Search by admin email"
                value={filters.adminEmail}
                onChange={(e) => setFilters({ ...filters, adminEmail: e.target.value })}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="targetUserEmail">User Email</label>
              <input
                id="targetUserEmail"
                type="text"
                placeholder="Search by user email"
                value={filters.targetUserEmail}
                onChange={(e) => setFilters({ ...filters, targetUserEmail: e.target.value })}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="companyName">Company Name</label>
              <input
                id="companyName"
                type="text"
                placeholder="Search by company name"
                value={filters.companyName}
                onChange={(e) => setFilters({ ...filters, companyName: e.target.value })}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                {STATUS_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="filter-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </button>
            <button type="button" className="btn-secondary" onClick={handleReset}>
              Reset
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {loading && logs.length === 0 ? (
        <TableSkeleton rows={10} columns={[160, 180, 180, 120, 140, 100]} label="Loading impersonation logs" />
      ) : (
        <>
          <div className="logs-count">
            Showing {filteredLogs.length} log{filteredLogs.length !== 1 ? 's' : ''} (out of {logs.length} loaded)
          </div>

          <div className="table-wrap">
            <table className="data-table impersonation-logs-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Admin</th>
                  <th>User</th>
                  <th>Company</th>
                  <th>Status</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="no-logs">
                      No impersonation logs found. Try adjusting your filters.
                    </td>
                  </tr>
                ) : (
                  filteredLogs.map(log => (
                    <tr 
                      key={log.id}
                      className="impersonation-log-row"
                      onClick={() => setSelectedLog(log)}
                    >
                      <td className="timestamp-cell">
                        {formatDate(log.createdAt || log.timestamp)}
                      </td>
                      <td className="admin-cell">
                        <div className="admin-info">
                          <span className="admin-email">{log.adminEmail || 'Unknown admin'}</span>
                          {(log.adminId || log.adminUid) && (
                            <span className="admin-id">{log.adminId || log.adminUid}</span>
                          )}
                        </div>
                      </td>
                      <td className="user-cell">
                        <div className="user-info">
                      <span className="user-email">
                        {resolveUserEmail(log) || 'Unknown user'}
                      </span>
                          {log.targetUserId && (
                            <span className="user-id">{log.targetUserId}</span>
                          )}
                        </div>
                      </td>
                      <td className="company-cell">
                        <div className="company-info">
                          <span className="company-name">
                            {resolveCompanyName(log) || 'N/A'}
                          </span>
                          {(log.companyId || log.targetCompanyId) && (
                            <span className="company-id">{log.companyId || log.targetCompanyId}</span>
                          )}
                        </div>
                      </td>
                      <td className="status-cell">
                        {(() => {
                          const status = deriveStatus(log);
                          return (
                            <span className={formatStatusBadgeClass(status)}>
                              {getStatusLabel(status)}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="details-cell">
                        {log.notes && (
                          <div className="meta-detail">
                            <strong>Notes:</strong> {log.notes}
                          </div>
                        )}
                        {(log.errorMessage || log.error) && (
                          <div className="error-detail">Error: {log.errorMessage || log.error}</div>
                        )}
                        {!log.notes && !log.errorMessage && !log.error && log.metadata?.code && (
                          <div className="meta-detail">Code: {log.metadata.code}</div>
                        )}
                        {!log.notes && !log.errorMessage && !log.error && !log.metadata?.code && (
                          <span className="meta-detail">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div className="load-more-container">
              <button
                className="btn-load-more"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}

      {selectedLog && (
        <div 
          className="impersonation-log-modal-backdrop"
          onClick={() => setSelectedLog(null)}
        >
          <div 
            className="impersonation-log-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Impersonation Session Details</h2>
            <div className="modal-section">
              <strong>Timestamp:</strong>{' '}
              {formatDate(selectedLog.createdAt || selectedLog.timestamp)}
            </div>
            <div className="modal-section">
              <strong>Admin:</strong>{' '}
              {selectedLog.adminEmail || 'Unknown admin'}
              {(selectedLog.adminId || selectedLog.adminUid) && (
                <>
                  <br />
                  <span className="modal-id-label">
                    ID: {selectedLog.adminId || selectedLog.adminUid}
                  </span>
                </>
              )}
            </div>
            <div className="modal-section">
              <strong>User:</strong>{' '}
              {resolveUserEmail(selectedLog) || 'Unknown user'}
              {selectedLog.targetUserId && (
                <>
                  <br />
                  <span className="modal-id-label">
                    ID: {selectedLog.targetUserId}
                  </span>
                </>
              )}
            </div>
            <div className="modal-section">
              <strong>Company:</strong>{' '}
              {resolveCompanyName(selectedLog) || 'N/A'}
              {(selectedLog.companyId || selectedLog.targetCompanyId) && (
                <>
                  <br />
                  <span className="modal-id-label">
                    ID: {selectedLog.companyId || selectedLog.targetCompanyId}
                  </span>
                </>
              )}
            </div>
            <div className="modal-section">
              <strong>Status:</strong>{' '}
              {getStatusLabel(deriveStatus(selectedLog))}
            </div>
            {selectedLog.notes && (
              <div className="modal-section">
                <strong>Notes:</strong>
                <pre className="modal-notes">
{selectedLog.notes}
                </pre>
              </div>
            )}
            {(selectedLog.errorMessage || selectedLog.error) && (
              <div className="modal-section">
                <strong>Error:</strong>{' '}
                {selectedLog.errorMessage || selectedLog.error}
              </div>
            )}
            {!selectedLog.notes && !selectedLog.errorMessage && !selectedLog.error && (
              <div className="modal-section modal-empty">
                No additional details recorded for this session.
              </div>
            )}
            <button 
              type="button" 
              className="btn-secondary modal-close-btn" 
              onClick={() => setSelectedLog(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImpersonationLogs;


